import React, { useState } from 'react';
import { signInWithEmailAndPassword, getMultiFactorResolver, TotpMultiFactorGenerator } from 'firebase/auth';
import { useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { Box, Button, TextField, Typography, Container, Paper, Alert, CircularProgress } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Grid, Link } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [mfaCode, setMfaCode] = useState('');
    const [showMfaChallenge, setShowMfaChallenge] = useState(false);
    
    const location = useLocation();
    const successMessage = location.state?.message;
    const { setMfaResolver, setUnverifiedUser, handleMfaVerification } = useAuth();

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        if (!email || !password) {
            setError("Email and password are required.");
            setLoading(false);
            return;
        }
        try {
            await signInWithEmailAndPassword(auth, email, password);
       } catch (err) {
            console.error("[LOGIN] Error:", err);
            if (err.code === 'auth/multi-factor-auth-required') {
                const resolver = getMultiFactorResolver(auth, err);
                setMfaResolver(resolver);
                setUnverifiedUser(err.customData?.user || null);
                setShowMfaChallenge(true);
                setLoading(false);
            } else if (err.code === 'auth/password-does-not-meet-requirements') {
                setError("Your password no longer meets our security requirements. Please use the 'Forgot password?' link below to set a new password (minimum 12 characters, with uppercase, lowercase, and a number).");
                setLoading(false);
            } else {
                setError("Failed to sign in. Please check your email and password.");
                setLoading(false);
            }
        }
    };

    const handleMfaSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        
        if (!mfaCode || mfaCode.length !== 6) {
            setError("Please enter a valid 6-digit code.");
            setLoading(false);
            return;
        }
        
        try {
            await handleMfaVerification(mfaCode);
        } catch (err) {
            console.error("[LOGIN] 2FA verification error:", err);
            if (err.code === 'auth/invalid-verification-code') {
                setError("Invalid verification code. Please try again.");
            } else {
                setError("Failed to verify code. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <Paper elevation={6} sx={{ marginTop: 8, padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography component="h1" variant="h5">
                    Savvai Edge™
                </Typography>
                
                {!showMfaChallenge ? (
                    <Box component="form" onSubmit={handleLogin} noValidate sx={{ mt: 1 }}>
                        {successMessage && (
                            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                                {successMessage}
                            </Alert>
                        )}
                        {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            disabled={loading}
                        >
                            {loading ? 'Signing In...' : 'Sign In'}
                        </Button>
                        <Grid container justifyContent="flex-end">
                            <Grid item>
                                <Link component={RouterLink} to="/forgot-password" variant="body2">
                                    Forgot password?
                                </Link>
                            </Grid>
                        </Grid>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleMfaSubmit} noValidate sx={{ mt: 1 }}>
                        <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
                            Two-Factor Authentication is required for your account.
                        </Alert>
                        {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Enter the 6-digit code from your authenticator app:
                        </Typography>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="mfaCode"
                            label="Verification Code"
                            name="mfaCode"
                            autoComplete="one-time-code"
                            autoFocus
                            value={mfaCode}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                setMfaCode(value);
                            }}
                            onPaste={(e) => {
                                e.preventDefault();
                                const pastedText = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                                setMfaCode(pastedText);
                            }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && mfaCode.length === 6 && !loading) {
                                    handleMfaSubmit(e);
                                }
                            }}
                            disabled={loading}
                            inputProps={{
                                maxLength: 6,
                                style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.3rem' }
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2, position: 'relative' }}
                            disabled={loading || mfaCode.length !== 6}
                        >
                            Verify & Sign In
                            {loading && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        color: 'primary.light',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        marginTop: '-12px',
                                        marginLeft: '-12px',
                                    }}
                                />
                            )}
                        </Button>
                        <Button
                            fullWidth
                            variant="text"
                            onClick={() => {
                                setShowMfaChallenge(false);
                                setMfaCode('');
                                setError('');
                                setMfaResolver(null);
                                setUnverifiedUser(null);
                            }}
                            disabled={loading}
                        >
                            Back to Login
                        </Button>
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default Login;